import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import prosperQrAsset from "@/assets/prosper-qr-code.png.asset.json";

export const Route = createFileRoute("/es/precios-y-financiamiento")({
  head: () => {
    const base = esHead("financing");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(esWebPageSchema("financing")),
            },
          ]
        : [],
    };
  },
  component: EsFinancing,
});

function EsFinancing() {
  return (
    <EsPageShell
      page="financing"
      title="Cómo pagar la cirugía para bajar de peso: financiamiento y opciones de pago"
      intro="El costo no debería impedirle recibir la atención que necesita. Para quienes prefieren repartir el costo del tratamiento a lo largo del tiempo, ofrecemos acceso a financiamiento médico a través de Prosper® Healthcare Lending."
    >
      <EsSection id="como-funciona" heading="Cómo funciona el financiamiento con Prosper®">
        <p>
          Prosper® Healthcare Lending es un prestamista independiente especializado en
          financiar atención médica, dental, bariátrica, estética y otros procedimientos
          electivos. No es parte de nuestro consultorio.
        </p>
        <p>
          Prosper® ofrece préstamos personales sin garantía —es decir, no se exige ningún
          bien como respaldo— que usted puede usar para pagar el tratamiento por adelantado
          y después reembolsar en cuotas mensuales fijas. Los montos van de $2,000 a $50,000,
          con plazos de dos a seis años y sin penalización si decide pagar antes. Consultar
          su tasa toma unos minutos en línea y no afecta su puntaje de crédito. Los fondos
          aprobados pueden estar disponibles tan pronto como un día hábil después de la
          aprobación final.
        </p>
      </EsSection>

      <EsSection id="tasas" heading="Tasas, plazos y quién decide">
        <p>
          Como Prosper® es un prestamista externo, su tasa, el monto del préstamo y las
          condiciones los determina Prosper® según su historial y perfil financiero, no
          nuestro consultorio. Las tasas anuales (APR) actualmente van de 8.99% a 35.99% y
          puede aplicarse una comisión de originación de 1% a 9.99%; las tasas más bajas se
          otorgan a los solicitantes con mejor crédito. Todos los préstamos son emitidos por
          WebBank®.
        </p>
        <p>
          Le recomendamos revisar con calma las condiciones completas, el costo total de los
          intereses y el pago mensual, para tomar la decisión que mejor se ajuste a su
          presupuesto.
        </p>
        <p>
          <strong>Aviso importante:</strong> nuestro consultorio no otorga préstamos, no
          decide la aprobación y no recibe su información financiera. El financiamiento lo
          ofrecen únicamente Prosper® y WebBank®. Las tasas y condiciones están vigentes en
          2026 y pueden cambiar; confírmelas con el prestamista antes de solicitar.
        </p>
      </EsSection>

      <EsSection id="solicitar" heading="Solicite o consulte su tasa">
        <p className="text-center font-semibold text-foreground">
          Haga clic o escanee el código QR para comenzar la solicitud. Solicitar con
          Prosper<sup className="text-[0.7em]">®</sup> no perjudica su crédito.
        </p>
        <div className="text-center">
          <a
            href="https://www.prosper.com/personal-loans/healthcare-financing"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-block rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2"
          >
            <img
              src={prosperQrAsset.url}
              alt="Código QR de Prosper® Healthcare Lending que abre la solicitud de financiamiento para pacientes"
              width={1920}
              height={1920}
              loading="lazy"
              decoding="async"
              className="h-24 w-24 object-contain transition group-hover:opacity-90"
            />
          </a>
        </div>
        <p>
          Si prefiere que le expliquemos las opciones en español, llame al consultorio o
          escríbanos y con gusto le indicamos los precios de pago propio vigentes y cómo
          iniciar la solicitud.
        </p>
      </EsSection>

      <EsSection id="pago-propio" heading="Pago propio cuando el seguro no cubre">
        <p>
          Algunos planes excluyen por completo la cirugía bariátrica o los medicamentos para
          bajar de peso. En esos casos ofrecemos precios de pago propio con paquetes claros,
          que se pueden combinar con el financiamiento de Prosper®.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/es/seguro-para-cirugia-bariatrica"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Seguro para cirugía bariátrica
            </span>
            <span className="block text-sm text-muted-foreground">
              Requisitos plan por plan y cómo pedir la autorización previa.
            </span>
          </Link>
          <Link
            to="/es/seguro/pago-propio-y-negaciones"
            className="rounded-2xl border border-brand-soft p-4 text-left hover:bg-brand-soft/40"
          >
            <span className="block font-semibold text-brand-deep">
              Pago propio y apelaciones
            </span>
            <span className="block text-sm text-muted-foreground">
              Precios sin seguro y cómo apelar una negación.
            </span>
          </Link>
        </div>
      </EsSection>
    </EsPageShell>
  );
}
